import { watch, type Ref } from 'vue';

/**
 * Re-reads the caller's profile the moment the tenant becomes initialized (#1187).
 *
 * `initializeApp()` replaces `me` with the response of `GET /core/Authorization/InitializeApp`,
 * which the server builds in the same request that grants the caller the Administrateur role.
 * That `me` can therefore come back without the administration permission, and nothing reloads it
 * until the next page load: the setup assistant (gated on that permission) neither opens its
 * welcome dialog nor shows its button. A fresh `GET /core/me` after the transition carries the
 * permissions as they stand once initialization has committed.
 *
 * Only a profile already loaded as « not initialized » that turns initialized counts. At boot `me`
 * is `null` before the SDK loads it: that null -> initialized step is an ordinary load of an
 * already initialized tenant and must not cost a second `GET /core/me`.
 */
export function installMeRefreshOnTenantInit<TMe extends { isTenantInitialized?: boolean }>(sources: {
  me: Ref<TMe | null | undefined>;
  fetchMe: () => Promise<TMe>;
}): void {
  // null = not loaded yet, false = loaded on an uninitialized tenant, true = initialized.
  const state = () => (sources.me.value ? !!sources.me.value.isTenantInitialized : null);
  watch(state, async (now, before) => {
    if (before !== false || now !== true) return;
    try {
      sources.me.value = await sources.fetchMe();
    } catch (e) {
      // The permissions the page already holds stay in force; the next load corrects them.
      console.warn(e);
    }
  });
}
